import { useTranslation } from 'react-i18next';

interface MilestoneData {
  title: string;
  description: string;
  progress: number;
  targetDate: string;
  status: string;
  axis: string;
  successMetric: string;
  resources: string;
  barriers: string;
  internalBarriers?: string;
  externalBarriers?: string;
  notes: string;
  therapistNotes: string;
}

interface MilestoneModalsProps {
  showAddModal: boolean;
  showEditModal: boolean;
  editingMilestone: any;
  newMilestone: MilestoneData;
  onCloseAdd: () => void;
  onCloseEdit: () => void;
  onAdd: () => void;
  onUpdate: () => void;
  onDelete: (id: string) => void;
  onChange: (milestone: MilestoneData) => void;
  showConfirm: (message: string, onConfirm: () => void, confirmText?: string) => void;
  user?: { organizationRole?: string };
}

export function MilestoneModals({
  showAddModal,
  showEditModal,
  editingMilestone,
  newMilestone,
  onCloseAdd,
  onCloseEdit,
  onAdd,
  onUpdate,
  onDelete,
  onChange,
  showConfirm,
  user
}: MilestoneModalsProps) {
  const { t, i18n } = useTranslation();
  const isInstructor = user?.organizationRole === 'instructor';

  const renderMilestoneForm = (isEdit: boolean) => (
    <>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: '#000000', display: 'block', marginBottom: '5px' }}>{t('patientDetail.title')}</label>
        <input
          type="text"
          value={newMilestone.title}
          onChange={(e) => onChange({...newMilestone, title: e.target.value})}
          placeholder={t('patientDetail.enterMilestoneTitle')}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #ced4da',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        />
      </div>
      
      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: '#000000', display: 'block', marginBottom: '5px' }}>{t('patientDetail.description')}</label>
        <textarea
          value={newMilestone.description}
          onChange={(e) => onChange({...newMilestone, description: e.target.value})}
          placeholder={t('patientDetail.enterMilestoneDescription')}
          rows={3}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #ced4da',
            borderRadius: '6px',
            fontSize: '14px',
            resize: 'vertical'
          }}
        />
      </div>
      
      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: '#000000', display: 'block', marginBottom: '5px' }}>ציר</label>
        <select
          value={newMilestone.axis}
          onChange={(e) => onChange({...newMilestone, axis: e.target.value})}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #ced4da',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        >
          <option value="emotional">רגשי</option>
          <option value="occupational">תעסוקתי</option>
          <option value="rights">זכויות</option>
        </select>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: '#000000', display: 'block', marginBottom: '5px' }}>מדד הצלחה</label>
        <input
          type="text"
          value={newMilestone.successMetric}
          onChange={(e) => onChange({...newMilestone, successMetric: e.target.value})}
          placeholder="למשל: הורדה של חרדה מ8 פעמים בשבוע ל4 פעמים בשבוע"
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #ced4da',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: '#000000', display: 'block', marginBottom: '5px' }}>משאבים</label>
        <textarea
          value={newMilestone.resources}
          onChange={(e) => onChange({...newMilestone, resources: e.target.value})}
          placeholder="חוזקות/משאבים זמינים"
          rows={2}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #ced4da',
            borderRadius: '6px',
            fontSize: '14px',
            resize: 'vertical'
          }}
        />
      </div>

      {!isInstructor && (
        <div style={{ marginBottom: '15px' }}>
          <label style={{ color: '#000000', display: 'block', marginBottom: '8px', fontWeight: '600' }}>חסמים אפשריים</label>
          
          <div style={{ marginBottom: '12px' }}>
            <label style={{ color: '#000000', display: 'block', marginBottom: '5px', fontSize: '13px' }}>חסמים פנימיים</label>
            <textarea
              value={newMilestone.internalBarriers || ''}
              onChange={(e) => onChange({...newMilestone, internalBarriers: e.target.value})}
              placeholder="חסמים פנימיים (לדוגמה: חוסר ביטחון עצמי, פחדים, דפוסי חשיבה מעכבים)"
              rows={2}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ced4da',
                borderRadius: '6px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={{ color: '#000000', display: 'block', marginBottom: '5px', fontSize: '13px' }}>חסמים חיצוניים</label>
            <textarea
              value={newMilestone.externalBarriers || ''}
              onChange={(e) => onChange({...newMilestone, externalBarriers: e.target.value})}
              placeholder="חסמים חיצוניים (לדוגמה: קשיים כלכליים, מחסור בתמיכה משפחתית, חסמים במערכת)"
              rows={2}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ced4da',
                borderRadius: '6px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>
        </div>
      )}

      {isEdit && (
        <div style={{ marginBottom: '15px' }}>
          <label style={{ color: '#000000', display: 'block', marginBottom: '5px' }}>הערות מטפל</label>
          <textarea
            value={newMilestone.therapistNotes}
            onChange={(e) => onChange({...newMilestone, therapistNotes: e.target.value})}
            placeholder="הערות ומחשבות מטפל"
            rows={2}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #ced4da',
              borderRadius: '6px',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </div>
      )}

      {isEdit && (
        <div style={{ marginBottom: '15px' }}>
          <label style={{ color: '#000000', display: 'block', marginBottom: '5px' }}>{t('patientDetail.progress')}</label>
          <div style={{ display: 'flex', gap: '2px', marginBottom: '10px' }}>
            {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((value) => (
              <div
                key={value}
                onClick={() => onChange({...newMilestone, progress: value})}
                style={{
                  flex: 1,
                  height: '30px',
                  backgroundColor: newMilestone.progress >= value ? '#007acc' : '#e9ecef',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: newMilestone.progress >= value ? 'white' : '#666',
                  borderRadius: value === 0 ? '4px 0 0 4px' : value === 100 ? '0 4px 4px 0' : '0',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (newMilestone.progress < value) {
                    e.currentTarget.style.backgroundColor = '#cce7ff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (newMilestone.progress < value) {
                    e.currentTarget.style.backgroundColor = '#e9ecef';
                  }
                }}
              >
                {value}
              </div>
            ))}
          </div>
          <div style={{ fontSize: '14px', color: '#666', textAlign: 'center' }}>
            {t('patientDetail.currentProgress')}: {newMilestone.progress}%
          </div>
        </div>
      )}

      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: '#000000', display: 'block', marginBottom: '5px' }}>סטטוס</label>
        <select
          value={newMilestone.status}
          onChange={(e) => onChange({...newMilestone, status: e.target.value})}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #ced4da',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        >
          <option value="in_progress">בתהליך</option>
          <option value="achieved">הושג</option>
          <option value="frozen">בהקפאה</option>
          <option value="maintenance">שימור</option>
          <option value="stuck">נתקע</option>
        </select>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ color: '#000000', display: 'block', marginBottom: '5px' }}>{t('patientDetail.targetDate')}</label>
        <div style={{ position: 'relative', width: '100%' }}>
          <input
            type="date"
            className={`date-picker-input ${isEdit ? 'edit' : 'add'}-milestone-target-date`}
            value={newMilestone.targetDate}
            onChange={(e) => onChange({...newMilestone, targetDate: e.target.value})}
            style={{
              width: '100%',
              padding: '10px 12px',
              paddingRight: i18n.language === 'he' ? '12px' : '40px',
              paddingLeft: i18n.language === 'he' ? '40px' : '12px',
              border: '1px solid #ced4da',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
            onClick={() => {
              const dateInput = document.querySelector(`.${isEdit ? 'edit' : 'add'}-milestone-target-date`) as HTMLInputElement;
              if (dateInput) {
                dateInput.showPicker();
              }
            }}
          />
          <span
            style={{
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              ...(i18n.language === 'he' ? { left: '12px' } : { right: '12px' }),
              pointerEvents: 'auto',
              fontSize: '18px',
              cursor: 'pointer',
              zIndex: 1
            }}
            onClick={() => {
              const dateInput = document.querySelector(`.${isEdit ? 'edit' : 'add'}-milestone-target-date`) as HTMLInputElement;
              if (dateInput) {
                dateInput.showPicker();
              }
            }}
            title={t('patientDetail.targetDate')}
          >
            📅
          </span>
        </div>
      </div>
    </>
  );

  // Add Milestone Modal
  if (showAddModal) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '30px',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          position: 'relative'
        }}>
          <button
            onClick={onCloseAdd}
            style={{
              position: 'absolute',
              top: '15px',
              ...(i18n.language === 'he' ? { left: '15px' } : { right: '15px' }),
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
              e.currentTarget.style.color = '#000';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#666';
            }}
            title={t('patientDetail.close')}
          >
            ×
          </button>
          
          <h3 style={{ 
            color: '#000000', 
            marginBottom: '20px', 
            textAlign: 'center', 
            paddingRight: i18n.language === 'he' ? '40px' : '0',
            paddingLeft: i18n.language === 'he' ? '0' : '40px'
          }}>
            {t('patientDetail.addNewMilestone')}
          </h3>
          
          {renderMilestoneForm(false)}
          
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={onAdd}
              style={{
                background: '#007acc',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {t('patientDetail.addMilestoneButton')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Edit Milestone Modal
  if (showEditModal) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '90vh',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 30px 15px 30px',
            borderBottom: '1px solid #e9ecef',
            background: 'white',
            borderRadius: '12px 12px 0 0',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            direction: i18n.language === 'he' ? 'rtl' : 'ltr'
          }}>
            <h3 style={{ 
              color: '#000000', 
              margin: 0,
              textAlign: 'center',
              flex: 1
            }}>
              {t('patientDetail.editMilestone')}
            </h3>
            <button
              onClick={onCloseEdit}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
                e.currentTarget.style.color = '#000';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#666';
              }}
              title={t('patientDetail.close')}
            >
              ×
            </button>
          </div>
          
          <div style={{
            padding: '30px',
            overflowY: 'auto',
            flex: 1
          }}>
            {renderMilestoneForm(true)}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  if (editingMilestone) {
                    showConfirm(
                      t('patientDetail.confirmDeleteMilestone'),
                      () => onDelete(editingMilestone.id),
                      t('patientDetail.deleteMilestone')
                    );
                  }
                }}
                style={{
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {t('patientDetail.deleteMilestone')}
              </button>
              <button
                onClick={onUpdate}
                style={{
                  background: '#007acc',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {t('patientDetail.saveChanges')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
